import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Clock, Image as ImageIcon, Inbox, LogIn, PlusCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAppStore } from '../../../app/appStore';
import { useAuthStore } from '../../auth/authStore';
import { formatLocalTime } from '../../../shared/lib/datetime';
import { useI18nStore } from '../../../shared/i18n/i18nStore';
import { fetchFeedbackList, type FeedbackItem } from './api';
import { FeedbackDetail } from './FeedbackDetail';
import { FeedbackEmptyOwl } from './FeedbackIllustrations';
import { feedbackStatusPresentation, isFeedbackUnread } from './feedbackStatus';

/**
 * The Feedback tab: a persistent "submit feedback" button, the player's own
 * threads below it (each row: status tag + unread dot + first line + time +
 * image count), and — when a row (or activeFeedbackId) is selected — the
 * detail screen in its place. `onCompose` is owned by the page: Task 16
 * builds the actual compose flow, this task only wires the button up to it.
 */
export function FeedbackPanel({ onCompose }: { onCompose: () => void }) {
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const setRoute = useAppStore((state) => state.setRoute);
  const t = useI18nStore((state) => state.t);
  const activeFeedbackId = useAppStore((state) => state.activeFeedbackId);
  const setActiveFeedbackId = useAppStore((state) => state.setActiveFeedbackId);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // avisosViewForRoute already keeps a guest off this tab entirely (see its
  // own comment for why that's the single guard), so guestMode is checked
  // here only for defense — this is a click-time guard on one button, not a
  // second copy of the render-path decision that function owns. A signed-out,
  // non-guest session (e.g. a token that expired while this tab was already
  // open) is the case this actually exists to catch: without it, tapping
  // Submit would open FeedbackCompose only to have the request 401.
  function handleComposeClick() {
    if (!token || guestMode) {
      setShowLoginPrompt(true);
      return;
    }
    setShowLoginPrompt(false);
    onCompose();
  }

  // A caller elsewhere in the app (today: none yet, but the field exists for
  // a future notification deep link) can hand over a thread to open
  // directly. Consumed once and cleared, the same way avisosTargetView is —
  // so it never reopens the same thread again on a later render.
  useEffect(() => {
    if (activeFeedbackId != null) {
      setSelectedId(activeFeedbackId);
      setActiveFeedbackId(null);
    }
  }, [activeFeedbackId, setActiveFeedbackId]);

  const listQuery = useInfiniteQuery({
    queryKey: ['feedback', token],
    queryFn: ({ pageParam }) => fetchFeedbackList(token || '', pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    // Strictly-exclusive cursor (task-15 brief): the last loaded item's id,
    // nothing else. No client-side filtering on top of it.
    getNextPageParam: (lastPage) => {
      const items = lastPage.items || [];
      return items.length > 0 ? items[items.length - 1].id : undefined;
    },
    enabled: !!token,
  });

  if (selectedId != null) {
    return <FeedbackDetail id={selectedId} onBack={() => setSelectedId(null)} />;
  }

  const items = listQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const showEmpty = !listQuery.isLoading && !listQuery.isError && items.length === 0;

  return (
    <div className="feedback-panel">
      <button type="button" className="feedback-compose" onClick={handleComposeClick}>
        <PlusCircle size={18} aria-hidden="true" />
        {t('feedbackNew')}
      </button>

      {showLoginPrompt ? (
        <div className="feedback-state">
          <LogIn size={28} aria-hidden="true" />
          <strong>{t('feedbackLoginRequired')}</strong>
          <button type="button" onClick={() => setRoute('auth.login')}>
            {t('login')}
          </button>
        </div>
      ) : null}

      {listQuery.isLoading ? <FeedbackState title={t('loading')} /> : null}

      {listQuery.isError ? (
        <FeedbackState
          icon="error"
          title={t('avisosLoadError')}
          body={t('avisosLoadErrorBody')}
          actionLabel={t('retry')}
          onAction={() => listQuery.refetch()}
        />
      ) : null}

      {showEmpty && !showLoginPrompt ? (
        <FeedbackState
          icon="empty"
          title={t('feedbackEmpty')}
          body={t('feedbackEmptyBody')}
          actionLabel={t('feedbackNew')}
          onAction={handleComposeClick}
        />
      ) : null}

      {items.map((item) => (
        <FeedbackRow key={item.id} item={item} onClick={() => setSelectedId(item.id)} />
      ))}

      {listQuery.hasNextPage ? (
        <button
          type="button"
          className="feedback-load-more"
          onClick={() => listQuery.fetchNextPage()}
          disabled={listQuery.isFetchingNextPage}
        >
          {listQuery.isFetchingNextPage ? t('loading') : t('feedbackLoadMore')}
        </button>
      ) : null}
    </div>
  );
}

function FeedbackRow({ item, onClick }: { item: FeedbackItem; onClick: () => void }) {
  const t = useI18nStore((state) => state.t);
  const presentation = feedbackStatusPresentation(item.status);
  const unread = isFeedbackUnread(item);
  const StatusIcon = presentation.icon === 'check' ? Check : presentation.icon === 'clock' ? Clock : null;

  return (
    <button type="button" className="feedback-row" onClick={onClick}>
      <span className="feedback-row-main">
        <span className="feedback-row-top">
          <span className={`feedback-status ${presentation.toneClass}`}>
            {StatusIcon ? <StatusIcon size={11} aria-hidden="true" /> : null}
            {t(presentation.labelKey)}
          </span>
          {unread ? <i className="feedback-row-dot" aria-hidden="true" /> : null}
        </span>
        <span className="feedback-row-content">{item.content}</span>
        <span className="feedback-row-meta">
          <em>{formatLocalTime(item.created_at)}</em>
          {item.image_count > 0 ? (
            <span className="feedback-row-images">
              <ImageIcon size={11} aria-hidden="true" />
              {item.image_count}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}

function FeedbackState({
  title,
  body,
  icon,
  actionLabel,
  onAction,
}: {
  title: string;
  body?: string;
  icon?: 'error' | 'empty';
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="feedback-state">
      {icon === 'error' ? (
        <AlertTriangle size={28} aria-hidden="true" />
      ) : icon === 'empty' ? (
        <FeedbackEmptyOwl />
      ) : (
        <Inbox size={28} aria-hidden="true" />
      )}
      <strong>{title}</strong>
      {body ? <p>{body}</p> : null}
      {actionLabel && onAction ? (
        <button type="button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}
